import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import {
  Dialog,
  Box,
  Typography,
  Button,
  DialogActions,
  Grid,
  CircularProgress,
  useTheme,
  useMediaQuery
} from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import jsPDF from "jspdf";
import logo from "../../assets/xpdlogo.png";

const CardTransactionDetailsDialog = ({ open, onClose, transaction }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const transactionDetails = transaction;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatStatus = (status) => {
    switch (status) {
      case "COMPLETION":
        return { text: "Completed", color: "#198754" }; // Green
      case "PENDING":
        return { text: "Pending", color: "#ffc107" }; // Orange
      case "DECLINED":
        return { text: "Declined", color: "#dc3545" }; // Red
      default:
        return { text: "Unknown", color: "#6c757d" }; // Grey for unknown
    }
  };

  const handleDownloadPDF = () => {
    if (!transactionDetails) return;

    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    };

    const generatePDF = async () => {
      try {
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
          compress: true
        });

        const pageHeight = doc.internal.pageSize.height;
        const pageWidth = doc.internal.pageSize.width;
        let yPos = 20;

        // Add logo
        const addHeader = async () => {
          try {
            const logoImg = await loadImage(logo);
            const logoHeight = 25;
            const logoWidth = (logoImg.width * logoHeight) / logoImg.height;
            const xPos = (pageWidth - logoWidth) / 2;

            const canvas = document.createElement('canvas');
            canvas.width = logoImg.width;
            canvas.height = logoImg.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(logoImg, 0, 0);
            const logoData = canvas.toDataURL('image/png', 1.0);

            doc.addImage(logoData, 'PNG', xPos, yPos - 15, logoWidth, logoHeight, '', 'FAST');
            yPos += 30;
          } catch (error) {
            console.error('Error adding header:', error);
          }
        };

        await addHeader();
        // Add Receipt Number and Date with null checks
        doc.setFont("helvetica");
        doc.setFontSize(9);
        doc.setTextColor(128, 128, 128);
        doc.text(`Transaction Receipt #${transactionDetails?.transaction_id || 'N/A'}`, 20, yPos);
        yPos += 5;

        doc.text("DATE & TIME", 20, yPos);
        yPos += 5;

        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(transactionDetails?.date_of_transaction || 'N/A', 20, yPos);

        // Add Status with null check
        const status = formatStatus(transactionDetails?.status || "Unknown").text;
        doc.setFontSize(10);
        doc.setTextColor(
          formatStatus(transactionDetails?.status || "Unknown").color
        );
        doc.text(status, 170, yPos, { align: 'right' });
        yPos += 20;
        // Add transaction details
        doc.setFont("helvetica");
        doc.setFontSize(16);
        doc.setTextColor(84, 42, 116);
        doc.text("Transaction Details", pageWidth / 2, yPos, { align: 'center' });
        yPos += 15;

        const details = [
          { label: "Date", value: transactionDetails.date_of_transaction },
          { label: "UUID", value: transactionDetails.transaction_id },
          { label: "Ref #", value: transactionDetails.reference_number },
          { label: "Type", value: transactionDetails.transaction_type },
          { label: "Transfer Type", value: transactionDetails.transaction_mode },
          { label: "Status", value: formatStatus(transactionDetails.status).text },
          { label: "Merchant Name", value: transactionDetails.merchant_name },
          { label: "Description", value: transactionDetails.description },
          { label: "Amount", value: `$${transactionDetails.amount}` },
          { label: "Currency", value: transactionDetails.currency },
          { label: "Transaction Country", value: transactionDetails.transaction_country }
        ];

        details.forEach(detail => {
          if (yPos > pageHeight - 20) {
            doc.addPage();
            yPos = 20;
          }

          doc.setFontSize(10);
          doc.setTextColor(84, 42, 116);
          doc.text(detail.label, 20, yPos);

          doc.setTextColor(0);
          doc.text(detail.value || "N/A", 80, yPos);

          yPos += 10;
        });

        doc.save(`card-transaction-${transactionDetails.transaction_id}.pdf`);
      } catch (error) {
        console.error('Error generating PDF:', error);
        setError('Failed to generate PDF');
      }
    };

    generatePDF();
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          p: isMobile ? 2 : 3,
          margin: isMobile ? 1 : 'auto',
          width: isMobile ? 'calc(100% - 32px)' : undefined,
        },
      }}
    >
      {/* Header with Logo */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
        <img
          src={logo}
          alt="Logo"
          style={{
            height: isMobile ? '40px' : '60px',
            width: 'auto',
            marginBottom: isMobile ? '15px' : '20px'
          }}
        />
      </Box>
      {/* Add the receipt header section */}
      {transactionDetails && (
        <Box sx={{ mb: isMobile ? 2 : 4 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: 'grey.500' }}>
              Transaction Receipt #{transactionDetails?.transaction_id}
            </Typography>
            <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mb: 0.5 }}>
              DATE & TIME
            </Typography>
            <Typography variant="body1">
              {transactionDetails?.date_of_transaction || 'N/A'}
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: isMobile ? 0 : -4,
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center'
          }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                px: 2,
                py: 0.5,
                borderRadius: '50px',
                color: formatStatus(transactionDetails?.status).color,
              }}
            >
              {formatStatus(transactionDetails?.status).text}
            </Typography>
          </Box>
        </Box>
      )}
      <Typography
        variant={isMobile ? "h6" : "h5"}
        sx={{
          color: "#542a74",
          mb: 2,
          textAlign: "center",
          fontSize: isMobile ? '1.1rem' : undefined
        }}
      >
        Transaction Details
      </Typography>

      {error && (
        <Typography color="error" align="center" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ maxHeight: isMobile ? 'calc(100vh - 250px)' : undefined, overflowY: 'auto' }}>
        {transactionDetails && (
          <>
            <DetailRow label="Date" value={transactionDetails.date_of_transaction} isMobile={isMobile} />
            <DetailRow label="UUID" value={transactionDetails.transaction_id} isMobile={isMobile} />
            <DetailRow label="Ref #" value={transactionDetails.reference_number} isMobile={isMobile} />
            <DetailRow label="Type" value={transactionDetails.transaction_type} isMobile={isMobile} />
            <DetailRow label="Transfer Type" value={transactionDetails.transaction_mode} isMobile={isMobile} />
            <DetailRow
              label="Status"
              value={formatStatus(transactionDetails.status).text}
              valueColor={formatStatus(transactionDetails.status).color}
              isMobile={isMobile}
            />
            <DetailRow label="Merchant Name" value={transactionDetails.merchant_name} isMobile={isMobile} />
            <DetailRow label="Description" value={transactionDetails.description} isMobile={isMobile} />
            <DetailRow label="Amount" value={`$${transactionDetails.amount}`} isMobile={isMobile} />
            <DetailRow label="Currency" value={transactionDetails.currency} isMobile={isMobile} />
            <DetailRow label="Transaction Country" value={transactionDetails.transaction_country} isMobile={isMobile} />
          </>
        )}
      </Box>

      <DialogActions sx={{ mt: 3, px: isMobile ? 1 : 3, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 2 }}>
        <Button
          onClick={onClose}
          sx={{
            color: "grey.500",
            width: isMobile ? '100%' : 'auto'
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleDownloadPDF}
          startIcon={<DownloadIcon />}
          sx={{
            bgcolor: '#542a74',
            '&:hover': { bgcolor: '#441f5f' },
            width: isMobile ? '100%' : 'auto'
          }}
        >
          Download Receipt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Enhanced DetailRow component with mobile support
const DetailRow = ({ label, value, valueColor, isMobile }) => (
  <Box
    sx={{
      py: isMobile ? 1 : 1.5,
      px: isMobile ? 1 : 0,
      borderBottom: "1px solid #e0e0e0",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Grid container spacing={isMobile ? 1 : 2}>
      <Grid item xs={isMobile ? 12 : 4}>
        <Typography
          sx={{
            color: "#542a74",
            fontWeight: 600,
            textTransform: "capitalize",
            fontSize: isMobile ? '0.875rem' : undefined
          }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item xs={isMobile ? 12 : 8}>
        <Typography
          sx={{
            fontFamily: "monospace",
            wordBreak: "break-all",
            color: valueColor || 'inherit',
            fontSize: isMobile ? '0.875rem' : undefined
          }}
        >
          {value || "N/A"}
        </Typography>
      </Grid>
    </Grid>
  </Box>
);

export default CardTransactionDetailsDialog;
